import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Business } from "@/types/directory";
import { queryBusinesses } from "@/services/directoryService";
import BusinessCard from "@/components/base/BusinessCard";
import SectionHeading from "@/components/base/SectionHeading";

interface SpotNearbyBusinessesProps {
  districtSlug: string;
  districtName: string;
  spotName: string;
}

export default function SpotNearbyBusinesses({
  districtSlug,
  districtName,
  spotName,
}: SpotNearbyBusinessesProps) {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    queryBusinesses({ district: districtSlug, sort: "rating", page: 1, pageSize: 40 })
      .then((response) => {
        if (active) setBusinesses(response.items.slice(0, 4));
      })
      .catch(() => {
        if (active) setBusinesses([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [districtSlug]);

  if (!loading && businesses.length === 0) return null;

  return (
    <section className="mt-12 rounded-2xl bg-[#F5F7FA] p-6 md:p-8">
      <SectionHeading
        eyebrow="Plan your trip"
        title={`Eat, stay & shop near ${spotName}`}
        description={`Top-rated local businesses across ${districtName} to round off your visit.`}
        action={{ label: `All businesses in ${districtName}`, to: `/location/${districtSlug}` }}
      />

      {loading ? (
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="h-80 animate-pulse rounded-lg border border-background-200 bg-background-100"
            />
          ))}
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {businesses.map((business) => (
            <BusinessCard key={business.id} business={business} />
          ))}
        </div>
      )}

      <Link
        to={`/directory?district=${districtSlug}`}
        className="mt-6 inline-flex items-center gap-2 whitespace-nowrap text-sm font-semibold text-primary-700 transition-colors hover:text-primary-800"
      >
        Search the full directory
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-arrow-right-line text-base" />
        </span>
      </Link>
    </section>
  );
}